/**
 * Click-free envelope helpers. Every envelope starts from 0 and ends at exactly 0, using
 * exponential segments for natural decays and a tiny final linear ramp to true silence.
 */

/** "Silent enough" floor for exponential ramps (−80 dBFS); exponential ramps can't target 0. */
export const SILENCE = 1e-4;

/** Percussive: 0 → peak (linear over `attack`) → exponential fall to silence over `decay`. Returns end time. */
export function perc(p: AudioParam, t: number, peak: number, attack: number, decay: number): number {
  const a = Math.max(0.0005, attack);
  const pk = Math.max(SILENCE * 2, peak);
  const d = Math.max(0.005, decay);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(pk, t + a);
  p.exponentialRampToValueAtTime(SILENCE, t + a + d);
  p.linearRampToValueAtTime(0, t + a + d + 0.008);
  return t + a + d + 0.008;
}

/** Attack (linear) → hold → exponential release. Returns end time. */
export function ahr(p: AudioParam, t: number, peak: number, attack: number, hold: number, release: number): number {
  const a = Math.max(0.001, attack);
  const pk = Math.max(SILENCE * 2, peak);
  const h = Math.max(0, hold);
  const r = Math.max(0.01, release);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(pk, t + a);
  p.setValueAtTime(pk, t + a + h);
  p.exponentialRampToValueAtTime(SILENCE, t + a + h + r);
  p.linearRampToValueAtTime(0, t + a + h + r + 0.008);
  return t + a + h + r + 0.008;
}

/**
 * Exponential rise then exponential fall — the "approach and pass" contour of whooshes.
 * Returns end time.
 */
export function swell(p: AudioParam, t: number, peak: number, rise: number, fall: number): number {
  const pk = Math.max(SILENCE * 2, peak);
  const r = Math.max(0.005, rise);
  const f = Math.max(0.01, fall);
  p.setValueAtTime(0, t);
  p.linearRampToValueAtTime(SILENCE, t + 0.002);
  p.exponentialRampToValueAtTime(pk, t + r);
  p.exponentialRampToValueAtTime(SILENCE, t + r + f);
  p.linearRampToValueAtTime(0, t + r + f + 0.008);
  return t + r + f + 0.008;
}

/** Exponential glide between two strictly-positive values. */
export function glide(p: AudioParam, t: number, from: number, to: number, dur: number): void {
  p.setValueAtTime(Math.max(1e-3, from), t);
  p.exponentialRampToValueAtTime(Math.max(1e-3, to), t + Math.max(0.001, dur));
}
